import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from './Layout';
import { Lock, Unlock } from 'lucide-react';
import { Button } from './ui/button';
import { warmAgent } from '../lib/printAgent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Guard: só permite o PDV se houver caixa com status=open.
 * Caixa em pending_close (turno encerrado, aguardando contagem) NÃO bloqueia o PDV —
 * o operador pode abrir um novo caixa e a contagem fica para finalizar em
 * Fluxo de Caixa > Abrir/Fechar Caixa.
 */
export const RequireCashSession = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    warmAgent();
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/cash-sessions/current`, { withCredentials: true });
        setSession(data);
      } catch (err) {
        setSession(null);
      } finally {
        setLoading(false);
      }
    };
    check();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  // Turno encerrado: caixa em pending_close → o operador pode abrir um NOVO caixa
  // sem precisar finalizar a contagem antes. A contagem fica para fazer em
  // Fluxo de Caixa > Abrir/Fechar Caixa. Por isso aqui apenas tratamos como
  // "sem caixa ativo" e mostramos a tela de abrir novo.
  const effectiveSession = session && session.status === 'open' ? session : null;

  if (!effectiveSession) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full p-8" data-testid="pdv-blocked">
          <div className="bg-white rounded-lg border-2 border-yellow-400 p-10 max-w-md text-center shadow-lg">
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <Lock size={36} className="text-yellow-700" />
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-2">Caixa Fechado</h2>
            <p className="text-slate-600 mb-6">
              Você precisa <strong>abrir um caixa</strong> antes de operar no PDV.
              Cada usuário tem seu próprio caixa por turno.
            </p>
            <Button
              onClick={() => navigate('/cash-session')}
              className="bg-green-600 hover:bg-green-700 h-12 px-6 text-base"
              data-testid="go-open-cash"
            >
              <Unlock size={20} className="mr-2" /> Ir para Abertura de Caixa
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return children;
};
